import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Baby,
  Gamepad2,
  Grid2X2,
  Home,
  ExternalLink,
  ImageOff,
  Loader2,
  PawPrint,
  Pill,
  Search,
  Shirt,
  Smartphone,
  Sparkles,
  Star,
  Wand2,
  X,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { GrowthIntelligenceAd, GrowthIntelligenceBrand, SpyAd } from "../types/adsSpy.types";
import { growthIntelligenceService, mapGrowthAdToSpyAd } from "../services/growthIntelligence.service";
import { cn } from "@/lib/utils";

interface GrowthRadarViewProps {
  onCreateSimilar: (ad: SpyAd) => void;
  refreshSignal?: number;
  searchOpenSignal?: number;
}

type NicheKey = "all" | "supplements" | "technology" | "beauty" | "fashion" | "home" | "pets" | "games" | "babies";

const NICHES: Array<{ key: NicheKey; label: string; terms: string[]; icon: LucideIcon }> = [
  { key: "all", label: "Todos", terms: [], icon: Grid2X2 },
  {
    key: "supplements",
    label: "Suplementos",
    terms: ["supplements", "suplement", "vitamina", "proteina", "colageno", "magnesio", "omega", "creatina", "nutricion", "fitness"],
    icon: Pill,
  },
  {
    key: "technology",
    label: "Tecnología",
    terms: ["technology", "iphone", "celular", "telefono", "smart", "audifono", "cargador", "tv", "tecnologia", "gadget", "portatil"],
    icon: Smartphone,
  },
  {
    key: "beauty",
    label: "Belleza",
    terms: ["beauty", "belleza", "skincare", "piel", "cabello", "maquillaje", "cosmetic", "serum", "facial"],
    icon: Sparkles,
  },
  {
    key: "fashion",
    label: "Moda y accesorios",
    terms: ["fashion", "moda", "jean", "ropa", "camisa", "zapato", "tenis", "joya", "joyeria", "reloj", "bolso", "accesorio"],
    icon: Shirt,
  },
  {
    key: "home",
    label: "Hogar",
    terms: ["home", "hogar", "cocina", "casa", "mueble", "limpieza", "organizador", "homecenter", "herramienta", "olla"],
    icon: Home,
  },
  {
    key: "pets",
    label: "Mascotas",
    terms: ["pets", "mascota", "perro", "gato", "pet", "peludo", "collar", "concentrado", "integralpet"],
    icon: PawPrint,
  },
  {
    key: "games",
    label: "Juegos",
    terms: ["games", "juego", "gaming", "gamer", "consola", "playstation", "xbox", "nintendo", "juguete"],
    icon: Gamepad2,
  },
  {
    key: "babies",
    label: "Bebés",
    terms: ["bebe", "bebes", "baby", "babies", "infantil", "recien nacido", "pañal", "panal", "coche", "cuna", "tetero", "lactancia", "maternidad"],
    icon: Baby,
  },
];

const TOP_ADS_LIMIT = 20;
const RADAR_PAGE_SIZE = 24;

export default function GrowthRadarView({ onCreateSimilar, refreshSignal = 0, searchOpenSignal = 0 }: GrowthRadarViewProps) {
  const [brands, setBrands] = useState<GrowthIntelligenceBrand[]>([]);
  const [selectedNiche, setSelectedNiche] = useState<NicheKey>("all");
  const [expandedTextIds, setExpandedTextIds] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mediaErrors, setMediaErrors] = useState<Set<number>>(new Set());
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeSearchQuery, setActiveSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<GrowthIntelligenceAd[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [visibleCount, setVisibleCount] = useState(RADAR_PAGE_SIZE);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const loadRadar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await growthIntelligenceService.getRadar({
        limit: 80,
        ads_per_brand: 8,
      });
      setBrands(data);
    } catch (err) {
      console.error("Error loading growth radar:", err);
      setError("No pudimos cargar anuncios ahora. Revisa que conversation-engine este corriendo.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRadar();
  }, [loadRadar, refreshSignal]);

  useEffect(() => {
    setVisibleCount(RADAR_PAGE_SIZE);
  }, [selectedNiche, activeSearchQuery, brands, searchResults]);

  useEffect(() => {
    if (searchOpenSignal === 0) return;
    setSearchOpen(true);
    window.setTimeout(() => searchInputRef.current?.focus(), 0);
  }, [searchOpenSignal]);

  const topAds = useMemo(() => {
    const uniqueAds = new Map<string, GrowthIntelligenceAd>();

    brands.forEach((brand) => {
      brand.ads?.forEach((ad) => {
        if (!ad.is_active) return;
        const adWithBrand = {
          ...ad,
          brand_name: ad.brand_name || brand.name,
          brand_logo_url: ad.brand_logo_url || brand.logo_url,
        };
        const signature = creativeSignature(adWithBrand);
        if (uniqueAds.has(signature)) return;
        uniqueAds.set(signature, adWithBrand);
      });
    });

    const niche = NICHES.find((item) => item.key === selectedNiche) || NICHES[0];
    const ads = Array.from(uniqueAds.values());
    const filteredAds = niche.key === "all"
      ? ads
      : ads.filter((ad) => nicheMatches(ad, niche.terms));

    const rankedAds = filteredAds.sort(compareRadarAds);
    if (niche.key === "all") {
      return rankedAds;
    }
    return rankedAds.slice(0, TOP_ADS_LIMIT);
  }, [brands, selectedNiche]);

  const displayedTopAds = useMemo(() => topAds.slice(0, visibleCount), [topAds, visibleCount]);
  const hasMoreTopAds = !activeSearchQuery && visibleCount < topAds.length;

  const loadMoreTopAds = useCallback(() => {
    setVisibleCount((current) => Math.min(current + RADAR_PAGE_SIZE, topAds.length));
  }, [topAds.length]);

  useEffect(() => {
    if (activeSearchQuery || loading || error) return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMoreTopAds) {
          loadMoreTopAds();
        }
      },
      { rootMargin: "220px" }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [activeSearchQuery, error, hasMoreTopAds, loadMoreTopAds, loading]);

  const handleToggleFavorite = async (ad: GrowthIntelligenceAd) => {
    setBrands((current) => updateAd(current, ad.id, { is_favorite: !ad.is_favorite }));
    setSearchResults((current) => current.map((item) => item.id === ad.id ? { ...item, is_favorite: !ad.is_favorite } : item));
    try {
      if (ad.is_favorite) {
        await growthIntelligenceService.removeFavorite(ad.id);
      } else {
        await growthIntelligenceService.addFavorite(ad.id);
      }
    } catch (err) {
      console.error("Error toggling radar favorite:", err);
      setBrands((current) => updateAd(current, ad.id, { is_favorite: ad.is_favorite }));
      setSearchResults((current) => current.map((item) => item.id === ad.id ? { ...item, is_favorite: ad.is_favorite } : item));
    }
  };

  const handleSubmitSearch = async (event?: FormEvent<HTMLFormElement>) => {
    event?.preventDefault();
    const query = searchQuery.trim();
    if (query.length < 2) {
      searchInputRef.current?.focus();
      return;
    }

    setSearchLoading(true);
    setSearchError(null);
    setActiveSearchQuery(query);
    try {
      const ads = await growthIntelligenceService.searchAds({
        q: query,
        limit: 10,
      });
      setSearchResults(ads);
    } catch (err) {
      console.error("Error searching growth ads:", err);
      setSearchError("No pudimos buscar anuncios ahora. Intenta de nuevo en un momento.");
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleCloseSearch = () => {
    setSearchOpen(false);
    setSearchQuery("");
    setActiveSearchQuery("");
    setSearchResults([]);
    setSearchError(null);
    setSearchLoading(false);
  };

  const handleToggleText = (adId: number) => {
    setExpandedTextIds((current) => {
      const next = new Set(current);
      if (next.has(adId)) next.delete(adId);
      else next.add(adId);
      return next;
    });
  };

  return (
    <section
      className="w-full min-w-0 space-y-5"
      data-testid="growth-radar-view"
    >
      {searchOpen && (
        <div
          className="rounded-[28px] border border-gray-200 bg-white/80 p-2 shadow-sm shadow-gray-200/70 backdrop-blur"
          style={{ maxWidth: "min(100%, calc(100vw - 23rem))" }}
          data-testid="growth-radar-search-panel"
        >
          <form onSubmit={handleSubmitSearch} className="flex min-h-14 items-center gap-2 rounded-[24px] bg-white px-3 ring-1 ring-gray-100">
            <Search className="h-5 w-5 shrink-0 text-gray-400" />
            <input
              ref={searchInputRef}
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Buscar anuncios, marcas, ofertas o productos"
              className="min-w-0 flex-1 bg-transparent text-base font-medium text-gray-950 outline-none placeholder:text-gray-400"
              data-testid="growth-radar-search-input"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => {
                  setSearchQuery("");
                  setActiveSearchQuery("");
                  setSearchResults([]);
                  setSearchError(null);
                  searchInputRef.current?.focus();
                }}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-gray-400 transition hover:bg-gray-100 hover:text-gray-700"
                aria-label="Limpiar búsqueda"
              >
                <X className="h-4 w-4" />
              </button>
            )}
            <button
              type="submit"
              disabled={searchLoading || searchQuery.trim().length < 2}
              className="inline-flex h-10 shrink-0 items-center justify-center rounded-full bg-fluxi px-5 text-sm font-semibold text-white shadow-sm shadow-fluxi/20 transition hover:bg-[#4545D8] disabled:cursor-not-allowed disabled:opacity-45"
              data-testid="growth-radar-search-submit"
            >
              {searchLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Buscar"}
            </button>
            <button
              type="button"
              onClick={handleCloseSearch}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-gray-200 text-gray-500 transition hover:bg-gray-50 hover:text-gray-800"
              aria-label="Cerrar búsqueda"
              data-testid="growth-radar-search-close"
            >
              <X className="h-4 w-4" />
            </button>
          </form>
          {activeSearchQuery && (
            <div className="flex flex-col items-start gap-1 px-4 pb-1 pt-3">
              <p className="text-sm font-semibold text-gray-900">
                {searchLoading ? "Buscando..." : `${searchResults.length} resultado${searchResults.length === 1 ? "" : "s"} para "${activeSearchQuery}"`}
              </p>
              <button type="button" onClick={handleCloseSearch} className="text-sm font-semibold text-fluxi hover:text-[#4545D8]">
                Volver al radar
              </button>
            </div>
          )}
        </div>
      )}

      {!activeSearchQuery && (
        <div className="flex flex-wrap gap-2 pb-1">
          {NICHES.map((niche) => {
            const isActive = selectedNiche === niche.key;
            const Icon = niche.icon;
            return (
              <button
                key={niche.key}
                type="button"
                onClick={() => setSelectedNiche(niche.key)}
                className={cn(
                  "inline-flex h-10 shrink-0 items-center gap-2 rounded-xl border px-3.5 text-sm font-semibold transition",
                  isActive
                    ? "border-fluxi bg-fluxi text-white shadow-sm shadow-fluxi/20"
                    : "border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-white/80 hover:text-gray-950"
                )}
                data-testid={`growth-radar-niche-${niche.key}`}
              >
                <Icon className="h-4 w-4" strokeWidth={2.1} />
                {niche.label}
              </button>
            );
          })}
        </div>
      )}

      {loading && !activeSearchQuery && (
        <div className="w-full space-y-5 overflow-hidden" data-testid="growth-radar-loading">
          <div
            className="grid gap-4"
            style={{ gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))" }}
          >
            {Array.from({ length: 8 }).map((_, index) => (
              <div key={index} className="h-[440px] animate-pulse rounded-2xl bg-white" />
            ))}
          </div>
        </div>
      )}

      {error && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          {error}
        </div>
      )}

      {!loading && !error && activeSearchQuery && searchError && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          {searchError}
        </div>
      )}

      {!loading && !error && activeSearchQuery && !searchLoading && !searchError && searchResults.length === 0 && (
        <div className="flex min-h-[360px] flex-col items-center justify-center rounded-2xl border border-dashed border-gray-200 bg-white/70 text-center">
          <Search className="h-8 w-8 text-gray-300" />
          <p className="mt-3 text-sm font-semibold text-gray-800">No encontramos anuncios para esa búsqueda</p>
          <p className="mt-1 max-w-sm text-xs leading-relaxed text-gray-500">
            Prueba con una marca, producto, oferta o palabra clave más amplia.
          </p>
        </div>
      )}

      {!loading && !error && !activeSearchQuery && topAds.length === 0 && (
        <div className="flex min-h-[360px] flex-col items-center justify-center rounded-2xl border border-dashed border-gray-200 bg-white/70 text-center">
          <Search className="h-8 w-8 text-gray-300" />
          <p className="mt-3 text-sm font-semibold text-gray-800">No hay anuncios para este nicho todavía</p>
          <p className="mt-1 max-w-sm text-xs leading-relaxed text-gray-500">
            Cuando el job semanal traiga más creativos de esta categoría aparecerán aquí automáticamente.
          </p>
        </div>
      )}

      {!error && activeSearchQuery && searchResults.length > 0 && (
        <RadarAdsGrid
          ads={searchResults}
          expandedTextIds={expandedTextIds}
          mediaErrors={mediaErrors}
          onMediaError={(adId) => setMediaErrors((current) => new Set(current).add(adId))}
          onFavorite={handleToggleFavorite}
          onToggleText={handleToggleText}
          onCreateSimilar={onCreateSimilar}
          testId="growth-radar-search-grid"
        />
      )}

      {!loading && !error && !activeSearchQuery && topAds.length > 0 && (
        <>
          <RadarAdsGrid
            ads={displayedTopAds}
            expandedTextIds={expandedTextIds}
            mediaErrors={mediaErrors}
            onMediaError={(adId) => setMediaErrors((current) => new Set(current).add(adId))}
            onFavorite={handleToggleFavorite}
            onToggleText={handleToggleText}
            onCreateSimilar={onCreateSimilar}
            testId="growth-radar-grid"
          />
          <div ref={sentinelRef} className="flex justify-center py-8" data-testid="growth-radar-sentinel">
            {hasMoreTopAds ? (
              <div className="flex items-center gap-3 opacity-60">
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-fluxi" />
                <span className="text-sm font-medium text-gray-400">Cargando</span>
              </div>
            ) : (
              <p className="text-xs text-gray-300">{topAds.length} anuncios</p>
            )}
          </div>
        </>
      )}
    </section>
  );
}

function RadarAdsGrid({
  ads,
  expandedTextIds,
  mediaErrors,
  onMediaError,
  onFavorite,
  onToggleText,
  onCreateSimilar,
  testId,
}: {
  ads: GrowthIntelligenceAd[];
  expandedTextIds: Set<number>;
  mediaErrors: Set<number>;
  onMediaError: (adId: number) => void;
  onFavorite: (ad: GrowthIntelligenceAd) => void;
  onToggleText: (adId: number) => void;
  onCreateSimilar: (ad: SpyAd) => void;
  testId: string;
}) {
  return (
    <div
      className="grid gap-3"
      style={{ gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))" }}
      data-testid={testId}
    >
      {ads.map((ad) => (
        <RadarCreativeCard
          key={ad.id}
          ad={ad}
          textExpanded={expandedTextIds.has(ad.id)}
          mediaError={mediaErrors.has(ad.id)}
          onMediaError={() => onMediaError(ad.id)}
          onFavorite={() => onFavorite(ad)}
          onToggleText={() => onToggleText(ad.id)}
          onCreateSimilar={() => onCreateSimilar(mapGrowthAdToSpyAd(ad))}
        />
      ))}
    </div>
  );
}

function RadarCreativeCard({
  ad,
  textExpanded,
  mediaError,
  onMediaError,
  onFavorite,
  onToggleText,
  onCreateSimilar,
}: {
  ad: GrowthIntelligenceAd;
  textExpanded: boolean;
  mediaError: boolean;
  onMediaError: () => void;
  onFavorite: () => void;
  onToggleText: () => void;
  onCreateSimilar: () => void;
}) {
  const mediaUrl = ad.thumbnail_url || ad.image_url || "";
  const isVideo = ad.media_type === "Video" && Boolean(ad.video_url);
  const canCreateSimilar = !isVideo && Boolean(ad.image_url || ad.thumbnail_url);
  const brandName = ad.brand_name || "Marca";
  const title = ad.headline || ad.ad_copy || "Creativo sin texto";
  const hasLongTitle = title.length > 34;
  const hasLongCopy = Boolean(ad.ad_copy && ad.ad_copy.length > 95);
  const brandInitial = brandName.trim().charAt(0).toUpperCase() || "F";

  return (
    <article
      className="flex min-w-0 flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
      data-testid={`growth-radar-ad-${ad.id}`}
    >
      <div className="relative aspect-[4/5] overflow-hidden bg-black">
        <div className="absolute inset-0 flex items-center justify-center">
          {!mediaError && isVideo ? (
            <video
              src={ad.video_url || undefined}
              poster={mediaUrl || undefined}
              className="h-full w-full object-contain"
              controls
              muted
              playsInline
              preload="metadata"
              onError={onMediaError}
            />
          ) : !mediaError && mediaUrl ? (
            <img
              src={mediaUrl}
              alt={ad.headline || brandName}
              className="h-full w-full object-contain"
              loading="lazy"
              onError={onMediaError}
            />
          ) : (
            <div className="flex h-full w-full flex-col items-center justify-center gap-2 bg-gray-100 text-gray-400">
              <ImageOff className="h-7 w-7" />
              <span className="text-xs font-medium">Sin imagen</span>
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={onFavorite}
          className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-white/90 shadow-sm backdrop-blur transition hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-fluxi/40"
          data-testid={`growth-radar-favorite-${ad.id}`}
          aria-label={ad.is_favorite ? "Quitar de favoritos" : "Guardar favorito"}
        >
          <Star className="h-4 w-4" color={ad.is_favorite ? "#E08700" : "#8A93A3"} fill={ad.is_favorite ? "#E08700" : "none"} />
        </button>
      </div>

      <div className="flex flex-1 flex-col gap-2.5 p-3.5">
        <div className="flex min-w-0 items-center gap-2">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-gray-100 text-[11px] font-bold text-gray-500 ring-1 ring-gray-200">
            {ad.brand_logo_url ? (
              <img
                src={ad.brand_logo_url}
                alt={brandName}
                className="h-full w-full object-cover"
                loading="lazy"
              />
            ) : (
              brandInitial
            )}
          </div>
          <p className="min-w-0 truncate text-[11px] font-semibold leading-none text-gray-400">{brandName}</p>
        </div>

        <div className="min-w-0 space-y-0.5">
          <button
            type="button"
            onClick={hasLongTitle ? onToggleText : undefined}
            className={cn(
              "block w-full text-left text-[13px] font-semibold leading-snug text-gray-950",
              hasLongTitle && "cursor-pointer hover:text-fluxi",
              !textExpanded && "truncate"
            )}
          >
            {title}
          </button>
        </div>

        {ad.ad_copy && (
          <div className="min-h-[44px]">
            <p className={cn("text-[11px] leading-snug text-gray-500", !textExpanded && "line-clamp-2 min-h-[30px]")}>
              {ad.ad_copy}
            </p>
            {hasLongCopy && (
              <button
                type="button"
                onClick={onToggleText}
                className="mt-0.5 inline-flex text-[11px] font-semibold text-fluxi hover:text-[#4545D8]"
              >
                {textExpanded ? "Ver menos" : "Ver más"}
              </button>
            )}
          </div>
        )}

        {!ad.ad_copy && <div className="min-h-[44px]" />}

        <div className="mt-auto flex items-center justify-between gap-2 border-t border-gray-100 pt-2.5">
          {canCreateSimilar ? (
            <button
              type="button"
              onClick={onCreateSimilar}
              className="inline-flex h-9 flex-1 items-center justify-center gap-1.5 rounded-xl bg-fluxi px-3 text-xs font-semibold text-white transition hover:bg-[#4545D8] focus:outline-none focus-visible:ring-2 focus-visible:ring-fluxi/40"
              data-testid={`growth-radar-create-${ad.id}`}
            >
              <Wand2 className="h-3.5 w-3.5" />
              Crear similar
            </button>
          ) : (
            <div className="inline-flex h-9 flex-1 items-center justify-center rounded-xl bg-gray-100 px-3 text-xs font-semibold text-gray-400">
              {isVideo ? "Video de referencia" : "Sin imagen"}
            </div>
          )}

          {ad.landing_page_url ? (
            <a
              href={ad.landing_page_url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-gray-200 text-gray-500 transition hover:bg-gray-50"
              data-testid={`growth-radar-link-${ad.id}`}
              aria-label="Abrir anuncio"
            >
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          ) : null}
        </div>
      </div>
    </article>
  );
}

function compareRadarAds(a: GrowthIntelligenceAd, b: GrowthIntelligenceAd) {
  const aImpressionRank = normalizeImpressionRank(a.impressions_index);
  const bImpressionRank = normalizeImpressionRank(b.impressions_index);
  if (aImpressionRank !== bImpressionRank) return aImpressionRank - bImpressionRank;
  if (b.score !== a.score) return b.score - a.score;
  return new Date(b.last_seen_at).getTime() - new Date(a.last_seen_at).getTime();
}

function normalizeImpressionRank(value?: number | null) {
  if (typeof value === "number" && value >= 0) return value;
  return Number.MAX_SAFE_INTEGER;
}

function creativeSignature(ad: GrowthIntelligenceAd) {
  return [
    ad.brand_name,
    ad.media_type,
    mediaFingerprint(ad),
    ad.headline,
    ad.ad_copy,
  ]
    .map((value) => normalizeSearchText(value || "").slice(0, 120))
    .join("|");
}

function mediaFingerprint(ad: GrowthIntelligenceAd) {
  const url = ad.video_url || ad.image_url || ad.thumbnail_url || "";
  if (!url) return "";
  try {
    const parsed = new URL(url);
    return parsed.pathname;
  } catch {
    return url.split("?")[0];
  }
}

function nicheMatches(ad: GrowthIntelligenceAd, terms: string[]) {
  const text = normalizeSearchText([
    ad.brand_name,
    ad.category,
    ad.headline,
    ad.ad_copy,
    ad.cta_text,
    ad.landing_page_url,
    ad.source_key,
  ].filter(Boolean).join(" "));

  return terms.some((term) => text.includes(normalizeSearchText(term)));
}

function normalizeSearchText(value: string) {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function updateAd(
  brands: GrowthIntelligenceBrand[],
  adId: number,
  patch: Partial<GrowthIntelligenceAd>
) {
  return brands.map((brand) => ({
    ...brand,
    ads: brand.ads?.map((ad) => (ad.id === adId ? { ...ad, ...patch } : ad)),
  }));
}
